/**
 * Swym (Wishlist Plus) glue. Two jobs, nothing else:
 *
 * 1. Add `swym-loaded` to <html> once the SDK reports in, so the card hearts
 *    and the header links — hidden by CSS until then — never show as dead
 *    controls on a storefront where the app embed is off or still loading.
 * 2. Re-bind hearts on markup that arrives after load. Swym binds
 *    `[data-swaction]` once at init; Horizon re-renders product grids through
 *    the section renderer (filters, pagination, search, mega-nav cards), and
 *    those hearts would otherwise be inert. The live theme did the same from
 *    async-product-cards.js; this is the Horizon-shaped version.
 *
 * The theme wires up no wishlist behaviour of its own. Swym binds the card
 * buttons by `[data-swaction]` and the header links by the `#swym-wishlist`
 * hash; both contracts are asserted in tests/unit/wishlist-contract.test.mjs,
 * because nothing here can be exercised without the app on the storefront.
 *
 * `SwymCallbacks` is the app's documented init hook and is safe to push to
 * before the SDK loads — which is the point, since this is a low-priority
 * module and the SDK is an app embed whose timing the theme does not control.
 *
 * `rebind()` disconnects the observer before calling into Swym and
 * reconnects in a `finally`. `initializeActionButtons` re-binds by scanning
 * the DOM, not by touching it, so nothing here has ever been observed to
 * insert a `[data-swaction]` node from inside that call — but the fix does
 * not depend on that holding. If it ever did, an observer left live across
 * the call would see its own insert, re-fire `rebind()`, and the pair could
 * self-sustain indefinitely. Disconnect/reconnect around the call removes
 * the possibility rather than relying on the callback staying well-behaved.
 *
 * Loaded from sections/echt-header.liquid (every page) and from
 * sections/echt-card-badges.liquid (the two grid templates). Loading it twice
 * costs nothing: the browser executes one module per URL.
 */
window.SwymCallbacks = window.SwymCallbacks || [];
window.SwymCallbacks.push((swat) => {
  document.documentElement.classList.add('swym-loaded');

  // Declared before `rebind` so the disconnect/reconnect below can close over
  // it; assigned once, further down, once the callback that references it
  // exists.
  let observer;

  // `initializeActionButtons` is Swym's own re-scan. The `ui.refresh` fallback
  // covers older SDK builds that never exposed it. Both are optional-chained
  // because this must not throw inside the app's callback queue — an exception
  // there would stop the callbacks Swym registered after ours.
  const rebind = () => {
    // Disconnected for the duration of the Swym call and reconnected in the
    // `finally` regardless of outcome, so a re-scan that touches the DOM can
    // never have this same observer see its own mutation and re-fire itself.
    observer.disconnect();
    try {
      if (typeof swat?.initializeActionButtons === 'function') swat.initializeActionButtons('[data-swaction]');
      else swat?.ui?.refresh?.();
    } finally {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  // Debounced, and filtered to mutations that actually carry a heart. The
  // section renderer replaces whole grids, so an unfiltered observer would run
  // Swym's re-scan for every unrelated DOM change on the page — the cart bubble
  // ticking over, a mega panel opening.
  let timer = 0;
  observer = new MutationObserver((records) => {
    const addsAHeart = records.some((record) =>
      [...record.addedNodes].some(
        (node) => node.nodeType === 1 && (node.matches?.('[data-swaction]') || node.querySelector?.('[data-swaction]'))
      )
    );
    if (!addsAHeart) return;
    clearTimeout(timer);
    timer = setTimeout(rebind, 100);
  });
  observer.observe(document.body, { childList: true, subtree: true });
});
